import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Controls, Panel, ReactFlow, ReactFlowProvider, ViewportPortal, applyEdgeChanges, applyNodeChanges, useReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { getNetworkEdges, getNetworkNodes } from "../../pages/Projects/ProjectDetails.helper";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import { Activity } from "../../swagger/caas-v1/models/Activity";
import APIError from "../APIError/APIError";
import { AssetNode } from "../CustomNodes/AssetNode";
import { ChargeStationNode } from "../CustomNodes/ChargeStationNode";
import { NodeIconColor } from "../CustomNodes/CustomNodeEnums";
import { UtilityPowerNode } from "../CustomNodes/UtilityPowerNode";
import NoDataMessage from "../DataError/NoDataMessage";
import "./NetworkDiagram.css";
const NetworkDiagramUI = ({ assetTrendsDetails, networkApiStatus, onAssetSelect, onToggleScreen, projectId, refetchNetworkDetails, replayTimeline, replayTimelineValue }) => {
    const { assetChargeActivityData, networkEdges, networkNodes } = useProjectDetailsStore((state) => ({
        assetChargeActivityData: state.assetChargeActivityData,
        networkEdges: state.networkEdges,
        networkNodes: state.networkNodes
    }));
    const [nodes, setNodes] = useState(networkNodes);
    const [edges, setEdges] = useState(networkEdges);
    const [assetNetworkNodesDetails, setAssetNetworkNodesDetails] = useState([]);
    const [assetNetworkEdgesDetails, setAssetNetworkEdgesDetails] = useState([]);
    const { projectDetails } = useProjectDetailsStore((state) => ({
        projectDetails: state.projectDetails
    }));
    const tooltipShowOnlyAssetName = replayTimeline > 0;
    // Get timezone with fallback pattern to handle API field naming inconsistency
    // API may return timeZone (camelCase with capital Z), timezone (lowercase), or offset
    // Using bracket notation to access potentially undefined property at runtime
    const projectTimezone = projectDetails?.["timeZone"] ||
        projectDetails?.timezone ||
        projectDetails?.offset;
    const reactFlow = useReactFlow();
    const nodeTypes = useMemo(() => ({
        assetNode: AssetNode,
        chargeStationNode: ChargeStationNode,
        utilityPowerNode: UtilityPowerNode
    }), []);
    const onNodesChange = useCallback((changes) => {
        setNodes((nds) => applyNodeChanges(changes, nds));
        reactFlow.fitView();
    }, [setNodes]);
    const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), [setEdges]);
    const onNodeClick = (_event, select) => {
        projectDetails?.AssetDetails.map((data) => {
            if (data?.assetId === select?.id) {
                onAssetSelect(data);
            }
        });
    };
    const convertToSeconds = (timestamp) => {
        const date = new Date(timestamp);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        // Convert hours and minutes to milliseconds
        const milliseconds = hours * 3600 + minutes * 60;
        return milliseconds;
    };
    useEffect(() => {
        if (replayTimeline) {
            const assetNetworkNodes = [], assetNetworkEdges = [], chargingSourceAssets = [];
            projectDetails?.AssetDetails.forEach((assetDetail) => {
                if (assetDetail.assetSourceType === "ESS") {
                    assetDetail["assetTransitDirection"] = "AtSource";
                }
                else if (assetDetail.assetSourceType === "MACHINE") {
                    assetDetail["assetTransitDirection"] = "AtSite";
                }
                assetDetail.activity = Activity.IDLE;
                assetDetail["chargingSource"] = null;
                assetNetworkNodes.push(assetDetail);
                if (assetDetail.assetSourceType === "CHARGING_STATION") {
                    chargingSourceAssets.push(assetDetail);
                }
            });
            assetChargeActivityData.forEach((assetChargeActivity) => {
                if (assetChargeActivity.chargingSource) {
                    assetNetworkEdges.push(assetChargeActivity);
                }
            });
            const updatedNetworkNodes = getNetworkNodes(assetNetworkNodes, projectDetails?.projectName, projectDetails?.projectId, projectDetails?.AssetDetails, assetTrendsDetails, tooltipShowOnlyAssetName, projectTimezone);
            const updatedNetworkEdges = getNetworkEdges(chargingSourceAssets);
            setNodes(updatedNetworkNodes);
            setEdges(updatedNetworkEdges);
            setAssetNetworkNodesDetails(assetNetworkNodes);
            setAssetNetworkEdgesDetails(chargingSourceAssets);
        }
        else {
            setNodes(networkNodes);
            setEdges(networkEdges);
        }
    }, [networkNodes, networkEdges, replayTimeline]);
    useEffect(() => {
        if (assetNetworkNodesDetails.length > 0) {
            const updatedNetworkEdges = assetNetworkEdgesDetails.map((obj) => ({ ...obj }));
            const assetNetworkNodes = assetNetworkNodesDetails.map((obj) => ({ ...obj }));
            assetChargeActivityData.forEach((networkEdge) => {
                const { activity = "", activityEndTimeStamp = "", activityStartTimeStamp = "", assetId = "", assetSourceType = "", assetTransitDirection = "", chargingSource = "" } = networkEdge;
                if (assetSourceType !== "CHARGING_STATION" && activityStartTimeStamp && activityEndTimeStamp) {
                    const startTimeInSeconds = convertToSeconds(networkEdge.activityStartTimeStamp);
                    const endTimeInSeconds = convertToSeconds(networkEdge.activityEndTimeStamp);
                    if (replayTimelineValue >= startTimeInSeconds && replayTimelineValue <= endTimeInSeconds) {
                        const networkNodeIndex = assetNetworkNodes.findIndex((networkNode) => networkNode.assetId === assetId);
                        if (networkNodeIndex !== -1) {
                            assetNetworkNodes[networkNodeIndex].activity = activity;
                            assetNetworkNodes[networkNodeIndex].activityStartTimeStamp = activityStartTimeStamp;
                            assetNetworkNodes[networkNodeIndex].activityEndTimeStamp = activityEndTimeStamp;
                            if (assetTransitDirection) {
                                assetNetworkNodes[networkNodeIndex].assetTransitDirection = assetTransitDirection;
                            }
                            if (chargingSource) {
                                updatedNetworkEdges.push(networkEdge);
                                if (assetSourceType?.toUpperCase() === "MACHINE") {
                                    const csAssetIndex = assetNetworkNodes.findIndex((networkNode) => networkNode.assetId === chargingSource);
                                    if (csAssetIndex !== -1 &&
                                        assetTransitDirection &&
                                        assetNetworkNodes[csAssetIndex].assetSourceType?.toUpperCase() ===
                                            "ESS".toUpperCase()) {
                                        assetNetworkNodes[csAssetIndex].assetTransitDirection = "AtSite";
                                    }
                                }
                            }
                        }
                    }
                }
            });
            const updatedNetworkNodes = getNetworkNodes(assetNetworkNodes, projectDetails?.projectName, projectDetails?.projectId, projectDetails?.AssetDetails, assetTrendsDetails, tooltipShowOnlyAssetName, projectTimezone);
            setNodes(updatedNetworkNodes);
            setEdges(getNetworkEdges(updatedNetworkEdges));
        }
    }, [replayTimelineValue]);
    if (networkApiStatus === "error") {
        return _jsx(APIError, { refreshHandler: refetchNetworkDetails });
    }
    const NetworkDiagramStatus = () => {
        if (networkApiStatus === "pending") {
            return (_jsx("div", { className: "flex justify-center items-center h-full min-h-[40vh]", "data-test": "loading-indicator", children: _jsx(CatLoadingIndicator, {}) }));
        }
        return _jsx(NoDataMessage, {});
    };
    return (_jsx(_Fragment, { children: networkApiStatus === "pending" || (networkApiStatus === "success" && networkNodes.length === 0) ? (NetworkDiagramStatus()) : (_jsxs(ReactFlow, { className: "bg-gray-100", "data-test": "React-Flow", deleteKeyCode: null, edges: edges, fitView: true, nodeTypes: nodeTypes, nodes: nodes, nodesDraggable: false, onEdgesChange: onEdgesChange, onInit: (instance) => {
                setTimeout(() => {
                    instance.fitView();
                }, 0);
            }, onNodeClick: onNodeClick, onNodesChange: onNodesChange, proOptions: { hideAttribution: true }, children: [networkNodes.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(Panel, { className: "bg-gray-100 text-black border-none w-40 h-26 text-left p-2 network-diagram-legend-container", "data-test": "Panel", position: "bottom-left", children: [_jsx("div", { className: "text-gray-500 w-full text-xs font-sans pl-2 pb-2", "data-test": "legend", children: "Legend" }), _jsx("div", { children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "idle", children: "Idle" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.IDLE}` })] }) }), _jsx("div", { children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "discharging", children: "Discharging" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.DISCHARGING}` })] }) }), _jsx("div", { children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "charging", children: "Charging" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.CHARGING}` })] }) }), _jsx("div", { children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "transporting", children: "Transporting" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.TRANSPORTING}` })] }) })] }), _jsx(ViewportPortal, { children: _jsx("div", { className: "network-transit", children: _jsx(CatHeading, { "data-test": "transit-title", variant: "title", children: "Transit" }) }, projectId) }, projectId)] })), _jsx(Controls, { onFitView: () => onToggleScreen(), position: "bottom-right", showFitView: replayTimeline ? false : true, showInteractive: false })] })) }));
};
const NetworkDiagram = (props) => {
    return (_jsx(ReactFlowProvider, { children: _jsx(NetworkDiagramUI, { ...props }) }));
};
export default memo(NetworkDiagram);
